import * as React from "react";

import { FrameFactories, FrameViews } from "@com.mgmtp.a12.client/client-core/lib/core/frame";
import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { HiddenText } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { LayoutGridProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/layout-grid/main/layout-grid.api";
import { LayoutGrid } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/layout-grid/main/layout-grid.view";
import { ApplicationModel } from "@com.mgmtp.a12.client/client-core/lib/core/model";

import {
	DASHBOARD_ASSIGNED_PROCESSES_OVERVIEW_MODEL,
	DASHBOARD_RECENT_CHANGES_OVERVIEW_MODEL,
	DASHBOARD_UPCOMING_DEADLINES_OVERVIEW_MODEL
} from "../../modules/dashboard/constants";

const { Grid, Row, Column } = LayoutGrid;

const HALF_WIDTH: LayoutGridProps.ColumnSize = { sm: 12, md: 12, lg: 6 };

export const DashboardLayout: FrameViews.Layout = {
	visibleViews: "multiple",
	component: function DashboardLayout({
		views,
		regionReference,
		viewProvider,
		progressComponentProvider,
		errorBoundaryProvider
	}) {
		const assignedProcessesView = findViewForModel(views, DASHBOARD_ASSIGNED_PROCESSES_OVERVIEW_MODEL);
		const recentChangesView = findViewForModel(views, DASHBOARD_RECENT_CHANGES_OVERVIEW_MODEL);
		const upcomingDeadlinesView = findViewForModel(views, DASHBOARD_UPCOMING_DEADLINES_OVERVIEW_MODEL);

		return (
			<>
				<HiddenText ariaLevel={1} role="heading">
					Dashboard
				</HiddenText>
				<Grid className="dashboard" fitToParent disableNegativeMargin>
					<Row>
						{assignedProcessesView ? (
							<Column size={HALF_WIDTH}>
								<Row>
									{views.length > 0 ? (
										<ViewComponentWrapper
											view={views[0]}
											ariaLevel={2}
											regionReference={regionReference}
											viewProvider={viewProvider}
											progressComponentProvider={progressComponentProvider}
											errorBoundaryProvider={errorBoundaryProvider}
										/>
									) : (
										<></>
									)}
								</Row>
								<Row>
									{recentChangesView ? (
										<ViewComponentWrapper
											view={recentChangesView}
											ariaLevel={3}
											regionReference={regionReference}
											viewProvider={viewProvider}
											progressComponentProvider={progressComponentProvider}
											errorBoundaryProvider={errorBoundaryProvider}
										/>
									) : (
										<></>
									)}
								</Row>
							</Column>
						) : (
							<></>
						)}
						<Column size={HALF_WIDTH}>
							<Row>
								{upcomingDeadlinesView ? (
									<ViewComponentWrapper
										view={upcomingDeadlinesView}
										ariaLevel={4}
										regionReference={regionReference}
										viewProvider={viewProvider}
										progressComponentProvider={progressComponentProvider}
										errorBoundaryProvider={errorBoundaryProvider}
									/>
								) : (
									<></>
								)}
							</Row>
						</Column>
					</Row>
				</Grid>
			</>
		);
	}
};

function findViewForModel(views: View[], modelName: string) {
	return views.find(view => view.modelDescriptors?.some(descriptor => descriptor.name === modelName));
}

interface ViewComponentProps extends Omit<FrameViews.ProviderProps, "regionUiProvider" | "layoutProvider"> {
	readonly view: View;
	readonly regionReference: ApplicationModel.Region.Reference;
	readonly ariaLevel: number;
}

const ViewComponentWrapper: React.ComponentType<ViewComponentProps> = function ViewComponentWrapper({
	view,
	regionReference,
	viewProvider,
	progressComponentProvider,
	errorBoundaryProvider = FrameFactories.errorBoundaryProvider,
	ariaLevel
}: ViewComponentProps) {
	const { name, activityId, ...others } = view;
	const ViewComponent = viewProvider(name);

	const ProgressComponent = progressComponentProvider(regionReference, name);
	const ErrorBoundary = errorBoundaryProvider(regionReference, name);

	return (
		<ErrorBoundary activityId={activityId} key={name} ariaLevel={ariaLevel}>
			{ViewComponent.handleProgressIndicator ? (
				<ViewComponent
					name={name}
					activityId={activityId}
					ariaLevel={ariaLevel}
					{...others}
					ProgressComponent={ProgressComponent}
				/>
			) : (
				<ProgressComponent activityId={activityId}>
					<ViewComponent name={name} activityId={activityId} ariaLevel={ariaLevel} {...others} />
				</ProgressComponent>
			)}
		</ErrorBoundary>
	);
};
